import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { PermissionNode, PermissionVersionLog, SysRole } from '@/core';
import { configurePermissionContext, isEndAllowed, useReadState, type DataScope } from '@/core';
import * as roleApi from '@/api/role';
import { useAuthStore } from './auth';

const CURRENT_END = (import.meta.env.VITE_APP_END || 'ADMIN') as 'ADMIN' | 'BPO' | 'MINIAPP';

/** permission store — 菜单/按钮权限树、数据范围、角色权限版本与回滚 */
export const usePermissionStore = defineStore('permission', () => {
  const auth = useAuthStore();
  const roles = ref<SysRole[]>([]);
  const tree = ref<PermissionNode[]>([]);
  const versions = ref<PermissionVersionLog[]>([]);
  const loading = ref(false);

  /**
   * 三枚端点**各自一格读态**。原 `loadStaticData` 把 `/roles` 与 `/permissions/tree` 并进一次
   * `Promise.all`、外面只有 `try/finally`（**没有 catch**）：任一 403，两个数组都停在 `[]`，于是
   * 两块面板各自渲染「后端返回空数组，字典尚未初始化」——那是**第三种说法**（既不是「无权限」、
   * 也不是「真的没有」）。分枚之后，「角色读到了、权限树被拒」也只让权限树那一格说话。
   * 判据本体在 `core/utils/readState.ts`，本文件只接线。
   */
  const rolesRead = useReadState<SysRole>();
  const treeRead = useReadState<PermissionNode>();
  const versionsRead = useReadState<PermissionVersionLog>();
  /**
   * 角色权限矩阵**自成一格读态**：勾选集与数据范围都从它取，⛔ 不从角色列表行取
   * （行面不投这两枚，取到的是 undefined ⇒ 整棵权限树永远空、数据范围永远停在缺省）。
   */
  const matrix = ref<roleApi.RolePermissionMatrix | null>(null);
  const matrixRead = useReadState<roleApi.RolePermissionMatrix>();

  const granted = computed(() => new Set(auth.permissions));
  const dataScope = computed<DataScope | undefined>(() => auth.user?.dataScope);

  /** 端绑定互斥：外部角色仅绑专属端，登录态与路由守卫均按端校验 */
  const endAllowed = computed(() => {
    const binding = auth.user?.endBinding ?? [];
    return binding.length > 0 ? isEndAllowed(CURRENT_END, binding) : false;
  });

  async function loadStaticData(): Promise<void> {
    loading.value = true;
    // 两枚 `load()` 各自把异常落成读态、不外抛，所以这里不会再从 onMounted 里抛出去；
    // 但**并行**保留（不串行 await），免得为了读态把首屏打慢一倍。
    const [roleRows, treeRows] = await Promise.all([
      // 本屏是「平台权限管理 → 角色权限」：角色名册只取平台侧两枚（缺省档＝全量，供用户管理页那枚调用点用）
      rolesRead.load(() => roleApi.fetchRolesWithDetail('platform')),
      treeRead.load(() => roleApi.fetchPermissionTree()),
    ]);
    roles.value = roleRows;
    tree.value = treeRows;
    loading.value = false;
  }

  async function loadVersions(roleId: string): Promise<void> {
    // 先复位：换角色时若上一轮是 403，不复位就会把「无权限读取变更记录」挂在这一轮的空结果上
    versionsRead.reset();
    versions.value = await versionsRead.load(() => roleApi.fetchPermissionVersions(roleId));
  }

  /**
   * 取一枚角色的权限矩阵：勾选集与数据范围都从这一枚出（⛔ 不从角色列表行取——行面不投这两枚）。
   */
  async function loadRoleMatrix(roleId: string): Promise<void> {
    // 先复位：换角色时上一枚矩阵不许留在屏幕上（否则新角色会短暂显示旧角色的勾选）
    matrix.value = null;
    matrixRead.reset();
    // 读态机器只认数组与分页壳（单对象会被判畸形）⇒ 按 1 元数组收，再取第 0 枚
    const rows = await matrixRead.load(async () => {
      const payload = await roleApi.fetchRolePermissions(roleId);
      return payload ? [payload] : [];
    });
    matrix.value = rows[0] ?? null;
  }

  async function savePermissions(roleId: string, payload: roleApi.SavePermissionsPayload): Promise<number> {
    const result = await roleApi.saveRolePermissions(roleId, payload);
    await loadVersions(roleId);
    return result.permVersion;
  }

  async function rollback(versionId: string, reason: string, roleId: string): Promise<number> {
    const result = await roleApi.rollbackPermissionVersion(versionId, reason);
    await loadVersions(roleId);
    return result.permVersion;
  }

  return {
    roles,
    tree,
    versions,
    loading,
    /**
     * 三枚读态（Pinia 顶层的 ref 会被解包，视图里直接 `permission.rolesRead` 即是 `ReadState`）。
     * ⛔ 视图不得再用 `xxx.length === 0` 反推「读到了空」——那是本格存在的唯一理由。
     */
    rolesRead: rolesRead.state,
    treeRead: treeRead.state,
    versionsRead: versionsRead.state,
    matrix,
    matrixRead: matrixRead.state,
    loadRoleMatrix,
    granted,
    dataScope,
    endAllowed,
    loadStaticData,
    loadVersions,
    savePermissions,
    rollback,
  };
});

/** 把权限上下文注入 core，供 hasPerm / v-perm 使用（core 不依赖 store） */
export function wirePermissionContext(): void {
  configurePermissionContext(() => {
    const auth = useAuthStore();
    if (!auth.user) return null;
    return {
      permissions: auth.user.permissions,
      dataScope: auth.user.dataScope,
      endBinding: auth.user.endBinding,
      roleCode: auth.user.roleCode,
      builtInSuperAdmin: auth.user.roleCode === 'SUPER_ADMIN',
    };
  });
}
